import DevOverrideBanner from "@/components/DevOverrideBanner";
import DocumentTitle from "@/components/DocumentTitle";
import Head from "@/components/Head";
import LogoutButton from "@/components/LogoutButton";
import SideNav from "@/components/SideNav/SideNav";
import Button from "@/components/Ui/Button";
import Row from "@/components/Ui/Row";
import Spinner from "@/components/Ui/Spinner";
import ErrorCode from "@/components/portal/ErrorCode";
import { SidebarProvider } from "@/components/shadcn/sidebar";
import { Toaster } from "@/components/shadcn/sonner";
import { IS_PRODUCTION } from "@/constants/env";
import { useSesamyAuthTokenContext } from "@/contexts/sesamy-auth-token-context";
import { useVendorContext } from "@/contexts/vendor-context";
import useUniversalRouter from "@/hooks/use-universal-router";
import { useAuth0 } from "@auth0/auth0-react";
import { show } from "@intercom/messenger-js-sdk";
import { ReactNode, useEffect, useRef } from "react";

const Layout = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth0();
  const { accessToken } = useSesamyAuthTokenContext();
  const { vendors } = useVendorContext();

  const { pathname } = useUniversalRouter();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isAllGraphsPage = pathname.endsWith("/all-graphs");
  const isVendorSelectPage = pathname.endsWith("/");

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [pathname]);

  if (!user) return null;

  if (!accessToken && !isVendorSelectPage)
    return (
      <div className="flex h-full w-full items-center justify-center text-7xl">
        <Spinner />
      </div>
    );

  if (vendors && vendors.length === 0)
    return (
      <ErrorCode
        code={403}
        customTitle="You don't have access yet"
        customMessage="You don't currently have access to the Sesamy portal. Contact your account admin to request access, or reach out to our support team for help."
        customActions={
          <Row className="gap-3">
            <LogoutButton text="Use another account" variant="default" />
            <Button variant="secondary" onClick={show} type="button">
              Sesamy support
            </Button>
          </Row>
        }
      />
    );

  return (
    <>
      <Head />
      <DocumentTitle />
      {isVendorSelectPage && children}
      {!isVendorSelectPage && (
        <div
          ref={scrollRef}
          data-testid="main-scroll-wrapper"
          className="scrollbar-background h-full w-full overflow-x-auto overflow-y-scroll overscroll-none"
        >
          <div className="min-w-5xl">
            {isAllGraphsPage && children}
            {!isAllGraphsPage && (
              <SidebarProvider>
                <SideNav />
                <main className="border-border bg-background column-up-left mt-2 w-full gap-2 rounded-tl-xl border border-r-0 border-b-0 pb-[84px] pl-4">
                  <div className="flex w-full min-w-0 flex-1 flex-col items-center">
                    {children}
                  </div>
                </main>
              </SidebarProvider>
            )}
          </div>
        </div>
      )}
      <Toaster
        visibleToasts={4}
        position="top-right"
        toastOptions={{
          classNames: {
            description: "text-muted-foreground!",
            error: "[&>[data-icon]]:text-destructive!",
            success: "[&>[data-icon]]:text-beneficial!",
            warning: "[&>[data-icon]]:text-uncertain!",
            info: "[&>[data-icon]]:text-informative!",
          },
        }}
      />

      {!IS_PRODUCTION && <DevOverrideBanner />}
    </>
  );
};

export default Layout;
